import { CHECKLIST_TARGET } from '../../constants/tagManager';
import type { IDataCheck, IDataCheckGroup } from '../../interfaces/checks';
import type { IProspect } from '../../interfaces/prospects';
import type { IQuote } from '../../interfaces/quotes';
import { TRANSLATIONS_PROSPECTS } from '../../translations/prospects';
import { CommercialAddonType, type AddonTypeById } from '../../types/quotes';
import {
  formatProspectAddress,
  getBillingContact,
  getPrimaryContact,
  hasDistinctBillingContact
} from '../prospects/prospects';
import { quoteHasAppliedCensus } from '../quotes/quotes';

// ─── Prospect ────────────────────────────────────────────────────────────────
const toRequiredCheck = (id: string, labelKey: string, done: boolean, analyticsTarget?: string): IDataCheck => ({
  id,
  labelKey,
  done,
  required: !done,
  analyticsTarget
});

export const buildProspectActivationChecks = (prospect: IProspect): IDataCheck[] => {
  const primary = getPrimaryContact(prospect);
  const billing = getBillingContact(prospect);
  const { checks } = TRANSLATIONS_PROSPECTS.profile.activation;
  const id = `prospect-${prospect.id}`;

  const base: IDataCheck[] = [
    toRequiredCheck(`${id}-tax-id`, checks.taxId, !!prospect.documentValue, CHECKLIST_TARGET.company),
    toRequiredCheck(`${id}-industry`, checks.industry, !!prospect.industry, CHECKLIST_TARGET.company),
    toRequiredCheck(
      `${id}-address`,
      checks.businessAddress,
      !!formatProspectAddress(prospect),
      CHECKLIST_TARGET.company
    )
  ];

  if (hasDistinctBillingContact(prospect)) {
    return [
      ...base,
      toRequiredCheck(`${id}-primary-email`, checks.primaryContactEmail, !!primary?.email, CHECKLIST_TARGET.contact),
      toRequiredCheck(`${id}-billing-name`, checks.billingContactName, !!billing?.name, CHECKLIST_TARGET.billing),
      toRequiredCheck(`${id}-billing-title`, checks.billingContactTitle, !!billing?.title, CHECKLIST_TARGET.billing),
      toRequiredCheck(`${id}-billing-email`, checks.billingContactEmail, !!billing?.email, CHECKLIST_TARGET.billing),
      toRequiredCheck(`${id}-billing-phone`, checks.billingContactPhone, !!billing?.phone, CHECKLIST_TARGET.billing)
    ];
  }

  return [
    ...base,
    toRequiredCheck(`${id}-primary-name`, checks.primaryContactName, !!primary?.name, CHECKLIST_TARGET.contact),
    toRequiredCheck(`${id}-primary-title`, checks.primaryContactTitle, !!primary?.title, CHECKLIST_TARGET.contact),
    toRequiredCheck(`${id}-primary-email`, checks.primaryContactEmail, !!primary?.email, CHECKLIST_TARGET.contact),
    toRequiredCheck(`${id}-primary-phone`, checks.primaryContactPhone, !!primary?.phone, CHECKLIST_TARGET.contact)
  ];
};

export const buildProspectActivationGroup = (prospect: IProspect): IDataCheckGroup => ({
  id: `prospect-${prospect.id}`,
  titleKey: TRANSLATIONS_PROSPECTS.profile.activation.clientData,
  checks: buildProspectActivationChecks(prospect)
});

// ─── Quote ───────────────────────────────────────────────────────────────────

export const quoteHasCensus = (quote: IQuote): boolean => quoteHasAppliedCensus(quote);

export const quoteHasVoluntaryAddon = (quote: IQuote, addonTypeById: AddonTypeById): boolean =>
  quote.commercialAddons.some((addon) => addonTypeById.get(addon.commercialAddonId) === CommercialAddonType.Voluntary);

export const buildQuoteActivationChecks = (quote: IQuote, addonTypeById: AddonTypeById): IDataCheck[] => {
  const { checks } = TRANSLATIONS_PROSPECTS.detail.pendingActivation;
  const hasCensus = quoteHasCensus(quote);

  const result: IDataCheck[] = [
    {
      ...toRequiredCheck(`quote-${quote.id}-census`, checks.census, hasCensus, CHECKLIST_TARGET.employeeData),
      quoteId: quote.id
    }
  ];

  if (quoteHasVoluntaryAddon(quote, addonTypeById)) {
    const hasPayroll = !!quote.paymentDate && !!quote.frequency;
    result.push({
      ...toRequiredCheck(`quote-${quote.id}-payroll`, checks.payroll, hasPayroll, CHECKLIST_TARGET.payrollSchedule),
      quoteId: quote.id
    });
  }

  return result;
};

export const buildQuoteActivationGroup = (quote: IQuote, addonTypeById: AddonTypeById): IDataCheckGroup => ({
  id: `quote-${quote.id}`,
  title: quote.name,
  checks: buildQuoteActivationChecks(quote, addonTypeById)
});

export const areAllChecksDone = (groups: IDataCheckGroup[]): boolean =>
  groups.every((group) => group.checks.every((check) => check.done));

// ─── Activation readiness ────────────────────────────────────────────────────

export const getActivationQuotes = (quotes: IQuote[]): IQuote[] =>
  quotes.filter((quote) => quote.status === 'accepted');

export const buildActivationCheckGroups = (
  prospect: IProspect,
  quotes: IQuote[],
  addonTypeById: AddonTypeById,
  prospectGroup?: IDataCheckGroup | null
): IDataCheckGroup[] => [
  prospectGroup ?? buildProspectActivationGroup(prospect),
  ...getActivationQuotes(quotes).map((quote) => buildQuoteActivationGroup(quote, addonTypeById))
];

export const isReadyToActivate = (
  prospect: IProspect,
  quotes: IQuote[],
  addonTypeById: AddonTypeById,
  prospectGroup?: IDataCheckGroup | null
): boolean =>
  getActivationQuotes(quotes).length > 0 &&
  areAllChecksDone(buildActivationCheckGroups(prospect, quotes, addonTypeById, prospectGroup));
